import { bucketFor, TASK_BUCKETS, type TaskBucket } from './taskBuckets';

export type TaskView = 'open' | 'week' | 'done' | 'all';
interface FilterableTask {
  id: string; title: string; project_name?: string | null; project_id: string | null;
  priority: number; status: string; due_date: string | null; completed_at: number | null; updated_at: number; created_at?: number;
}
export interface TaskFilters { project?: string | null; priority?: string | null }
export interface TaskSort { key: string; dir: 'asc' | 'desc' }

/** Missing dates/projects stay last in both directions. Priority 1 is high. */
export function compareTasks(a: FilterableTask, b: FilterableTask, sort: TaskSort): number {
  const keyOf = (task: FilterableTask): string | number | null => {
    switch (sort.key) {
      case 'priority': return task.priority;
      case 'project': return task.project_name?.trim() || null;
      case 'created': return task.created_at ?? task.updated_at;
      default: return task.due_date;
    }
  };
  const ak = keyOf(a), bk = keyOf(b);
  if (ak === null || bk === null) {
    if (ak !== null) return -1;
    if (bk !== null) return 1;
  } else {
    const compared = typeof ak === 'string' && typeof bk === 'string'
      ? ak.localeCompare(bk, 'en', { sensitivity: 'base' }) : ak < bk ? -1 : ak > bk ? 1 : 0;
    if (compared) return compared * (sort.dir === 'asc' ? 1 : -1);
  }
  return a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }) || a.id.localeCompare(b.id);
}

export function taskList<T extends FilterableTask>(
  tasks: T[], query: string, filters: TaskFilters, view: TaskView, today: string, doneShown: number, sort?: TaskSort,
) {
  const q = query.trim().toLowerCase();
  const filtered = tasks.filter(t =>
    (!q || t.title.toLowerCase().includes(q) || (t.project_name ?? '').toLowerCase().includes(q)) &&
    (!filters.project || (filters.project === 'none' ? t.project_id === null : t.project_id === filters.project)) &&
    (!filters.priority || t.priority === 1));
  const done = filtered.filter(t => t.status === 'done')
    .sort((a, b) => sort ? compareTasks(a, b, sort) : (b.completed_at ?? b.updated_at) - (a.completed_at ?? a.updated_at));
  const buckets = new Map<TaskBucket, T[]>();
  for (const t of filtered.filter(t => t.status !== 'done')) {
    const b = bucketFor(t.due_date, today);
    buckets.set(b, [...(buckets.get(b) ?? []), t]);
  }
  let visibleBuckets: readonly TaskBucket[] = view === 'week' ? ['overdue', 'today', 'tomorrow', 'week'] : TASK_BUCKETS;
  if (sort) {
    for (const list of buckets.values()) list.sort((a, b) => compareTasks(a, b, sort));
    if (sort.key === 'due' && sort.dir === 'desc') {
      visibleBuckets = [...visibleBuckets.filter(bucket => bucket !== 'none').reverse(), ...(view === 'week' ? [] : ['none'] as const)];
    }
  }
  const open = visibleBuckets.flatMap(bucket => buckets.get(bucket) ?? []);
  if (sort && sort.key !== 'due') open.sort((a, b) => compareTasks(a, b, sort));
  const visible = [
    ...(view === 'done' ? [] : open),
    ...(view === 'done' || view === 'all' ? done.slice(0, doneShown) : []),
  ];
  return { done, open, buckets, visibleBuckets, visibleTaskIds: visible.map(t => t.id), count: visible.length };
}
